import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RadialMenu.css';
// Only need ActionType here potentially for onSelect, but option.action covers it
import { ActionType } from '../../pages/PlaygroundPage/PlaygroundPage'; 

export interface RadialMenuOption {
  action: ActionType;
  label: string;
}

export interface RadialMenuProps {
  centerX: number; 
  centerY: number;
  radius: number; 
  innerRadius: number; 
  onSelect: (action: ActionType) => void;
  onClose?: () => void;
  // Add options prop back
  options: RadialMenuOption[]; 
  // Remove context props
  // currentBetToCall: number;
  // currentStreet: Street;
  // playerPosition: PlaygroundPosition;
}

// --- Helper functions for SVG paths (restored) ---
function describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
  const startRad = (startAngle - 90) * Math.PI / 180.0;
  const endRad = (endAngle - 90) * Math.PI / 180.0;
  const startX = x + (radius * Math.cos(startRad));
  const startY = y + (radius * Math.sin(startRad));
  const endX = x + (radius * Math.cos(endRad));
  const endY = y + (radius * Math.sin(endRad));
  const largeArcFlag = Math.abs(endAngle - startAngle) <= 180 ? "0" : "1";
  const d = [
      "M", startX, startY, 
      "A", radius, radius, 0, largeArcFlag, 1, endX, endY
  ].join(" ");
  return d;        
}

function describeDonutSlice(x: number, y: number, outerRadius: number, innerRadius: number, startAngle: number, endAngle: number): string {
  const startRad = (startAngle - 90) * Math.PI / 180.0;
  const endRad = (endAngle - 90) * Math.PI / 180.0;
  const startOuterX = x + (outerRadius * Math.cos(startRad));
  const startOuterY = y + (outerRadius * Math.sin(startRad));
  const endOuterX = x + (outerRadius * Math.cos(endRad));
  const endOuterY = y + (outerRadius * Math.sin(endRad));
  const startInnerX = x + (innerRadius * Math.cos(startRad));
  const startInnerY = y + (innerRadius * Math.sin(startRad));
  const endInnerX = x + (innerRadius * Math.cos(endRad));
  const endInnerY = y + (innerRadius * Math.sin(endRad));
  const largeArcFlag = Math.abs(endAngle - startAngle) <= 180 ? "0" : "1";
  const d = [
      "M", startOuterX, startOuterY,
      "A", outerRadius, outerRadius, 0, largeArcFlag, 1, endOuterX, endOuterY,
      "L", endInnerX, endInnerY,
      "A", innerRadius, innerRadius, 0, largeArcFlag, 0, startInnerX, startInnerY,
      "Z"
  ].join(" ");
  return d;        
}
// --- End Helper Functions ---

// --- Define colors (restored) --- 
const actionColors: { [key in ActionType]?: string } = {
  fold: 'rgba(239, 68, 68, 0.9)',    // Red
  call: 'rgba(59, 130, 246, 0.9)',  // Blue
  check: 'rgba(59, 130, 246, 0.9)', // Blue 
  raise: 'rgba(245, 158, 11, 0.9)', // Orange
  bet: 'rgba(34, 197, 94, 0.9)',     // Green
  straddle: 'rgba(147, 51, 234, 0.9)', // Purple
};
const defaultSliceColor = 'rgba(100, 116, 139, 0.9)'; // Grey fallback

const radialMenuRadius = 80;            
const radialMenuInnerRadius = 45;

const RadialMenu: React.FC<RadialMenuProps> = ({ 
  centerX, 
  centerY,
  radius = 80,  // Default smaller radius
  innerRadius = 45,  // Default smaller inner radius
  onSelect,
  onClose,
  options, // Destructure options
  // Remove context props from destructuring
  // currentBetToCall,
  // currentStreet,
  // playerPosition,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState<{ x: number; y: number } | null>(null);
  const [hoveredAction, setHoveredAction] = useState<ActionType | null>(null);
  const menuRef = useRef<SVGGElement>(null);

  // Use the passed options prop directly
  const optionsToDisplay = options; 
  const numItems = optionsToDisplay.length;
  
  const angleStep = 360 / numItems;
  const textRadius = innerRadius + (radius - innerRadius) / 2; // Position text in middle of slice
  const dynamicFontSize = Math.max(8, radius / 4); // Adjust font size based on radius

  // Get action at angle
  const getActionAtAngle = useCallback((angle: number): ActionType | null => {
    // Normalize angle to 0-360
    const normalizedAngle = ((angle % 360) + 360) % 360;
    const sliceIndex = Math.floor(normalizedAngle / angleStep);
    return optionsToDisplay[sliceIndex]?.action || null;
  }, [angleStep, optionsToDisplay]);

  // Get angle from center to point
  const getAngleFromCenter = useCallback((x: number, y: number): number => {
    const dx = x - centerX;
    const dy = y - centerY;
    let angle = Math.atan2(dy, dx) * 180 / Math.PI + 90; // Adjust for our coordinate system
    return ((angle % 360) + 360) % 360; // Normalize to 0-360
  }, [centerX, centerY]);

  // Check if point is within menu radius
  const isPointInMenu = useCallback((x: number, y: number): boolean => {
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance >= innerRadius && distance <= radius;
  }, [centerX, centerY, innerRadius, radius]);

  // Handle mouse/touch events
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDragging(true);
    setDragStartPos({ x, y });
    
    if (isPointInMenu(x, y)) {
      const angle = getAngleFromCenter(x, y);
      const action = getActionAtAngle(angle);
      setHoveredAction(action);
    }
  }, [isPointInMenu, getAngleFromCenter, getActionAtAngle]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (isPointInMenu(x, y)) {
      const angle = getAngleFromCenter(x, y);
      const action = getActionAtAngle(angle);
      setHoveredAction(action);
    } else {
      setHoveredAction(null);
    }
  }, [isDragging, isPointInMenu, getAngleFromCenter, getActionAtAngle]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isDragging && hoveredAction) {
      onSelect(hoveredAction);
    }
    
    setIsDragging(false);
    setDragStartPos(null);
    setHoveredAction(null);
  }, [isDragging, hoveredAction, onSelect]);

  // Handle simple clicks (tap without drag)
  const handleSliceClick = useCallback((action: ActionType, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isDragging) {
      onSelect(action);
    }
  }, [isDragging, onSelect]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Early return after all hooks
  if (numItems === 0) return null;

  return (
    <AnimatePresence>
      <motion.g 
        ref={menuRef}
        className="radial-menu-container" 
        transform={`translate(${centerX}, ${centerY})`}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{ touchAction: 'none' }}
      >
        {/* Background circle for better touch target */}
        <circle
          cx={0}
          cy={0}
          r={radius + 10}
          fill="transparent"
          style={{ pointerEvents: 'all' }}
        />
        
        {optionsToDisplay.map((option, index) => {
          const startAngle = index * angleStep;
          const endAngle = (index + 1) * angleStep;
          const slicePath = describeDonutSlice(0, 0, radius, innerRadius, startAngle, endAngle);
          const sliceColor = actionColors[option.action] || defaultSliceColor;
          const isHovered = hoveredAction === option.action;
          const isSelected = isDragging && hoveredAction === option.action;

          const midAngle = startAngle + angleStep / 2;
          const midAngleRad = (midAngle - 90) * Math.PI / 180.0;
          const textX = textRadius * Math.cos(midAngleRad);
          const textY = textRadius * Math.sin(midAngleRad);
          const needsFlip = midAngle > 90 && midAngle < 270;
          const textRotation = needsFlip ? midAngle + 180 : midAngle;

          return (
            <motion.g 
              key={option.action}
              animate={{
                scale: isSelected ? 1.1 : isHovered ? 1.05 : 1,
              }}
              transition={{ duration: 0.1, ease: "easeOut" }}
              onPointerDown={(e) => handleSliceClick(option.action, e)}
              style={{ cursor: 'pointer' }}
            >
              <path
                className="radial-menu-slice"
                d={slicePath}
                style={{ 
                  fill: isSelected ? `${sliceColor}dd` : sliceColor,
                  stroke: isSelected ? '#fff' : isHovered ? '#ddd' : '#999',
                  strokeWidth: isSelected ? 2 : 1,
                  filter: isSelected ? 'drop-shadow(0 0 8px rgba(255,255,255,0.5))' : 'none'
                }}
              />
              <text 
                className="radial-menu-text"
                x={textX} 
                y={textY}
                transform={`rotate(${textRotation} ${textX} ${textY})`}
                textAnchor="middle" 
                dominantBaseline="middle"
                style={{ 
                  fontSize: `${dynamicFontSize}px`, 
                  pointerEvents: 'none',
                  fill: isSelected ? '#000' : '#fff',
                  fontWeight: isSelected ? 'bold' : 'normal'
                }}
              >
                {option.label}
              </text>
            </motion.g>
          );
        })}
        
        {/* Center circle for visual appeal */}
        <circle
          cx={0}
          cy={0}
          r={innerRadius}
          fill="rgba(0,0,0,0.3)"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={1}
        />
      </motion.g>
    </AnimatePresence>
  );
};

export default RadialMenu; 